import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Axios from "axios";

// UI Components
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';

// Icons
import { 
  EllipsisVerticalIcon, 
  EyeIcon, 
  ArchiveBoxIcon, 
  TrashIcon, 
  ClockIcon, 
  ChevronLeftIcon, 
  ChevronRightIcon, 
  MagnifyingGlassIcon 
} from '@heroicons/react/24/outline';

import { getClientDisplayEmail } from '../../utils/clientDisplayEmail';

const Datatable = ({ mode = 'dashboard', title = 'All Clients', showArchived = false, ...props }) => {
  const backEndUrl = process.env.REACT_APP_SERVER_ADDRESS;
  const navigate = useNavigate();

  // --- STATE ---
  const [clientID, setClientID] = useState();
  const [clientDocuments, setClientDocuments] = useState();
  
  // Dialog State
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  
  // Menu State
  const [openMenuRowId, setOpenMenuRowId] = useState(null);
  const menuRef = useRef(null);

  // Filter State
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 9;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuRowId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // --- ACTIONS ---

  const handleRowClick = (id, isTemp) => {
    if (!isTemp) {
      navigate(`/dashboard/client/${id}`);
    }
  };

  const handleArchive = (row) => {
    Axios.put(`${backEndUrl}/updateClient/archiveClient/${row._id}`, {}, {
      withCredentials: true
    })
    .then(() => window.location.reload())
    .catch(err => alert("Error archiving client."));
  };

  const openDeleteModal = (client) => {
    setClientID(client._id);
    setClientDocuments(client.documents);
    setOpenDeleteDialog(true);
    setOpenMenuRowId(null);
  };

  const handleConfirmDelete = async() => {
    try {
      await Axios({
        method: 'DELETE',
        withCredentials: true,
        data: {
            client_id: clientID,
            clientDocuments: clientDocuments,
            consultantStorage: props.consultantStorage,
        },
        url: `${backEndUrl}/updateClient/deleteClient`,
      });
      setOpenDeleteDialog(false);
      window.location.reload();
    } catch {
    }  
  };

  // --- HELPERS (Visuals) ---

  const getAvatarColor = (name) => {
    const colors = ['bg-red-100 text-red-600', 'bg-orange-100 text-orange-600', 'bg-amber-100 text-amber-600', 'bg-emerald-100 text-emerald-600', 'bg-teal-100 text-teal-600', 'bg-cyan-100 text-cyan-600', 'bg-blue-100 text-blue-600', 'bg-indigo-100 text-indigo-600', 'bg-violet-100 text-violet-600', 'bg-fuchsia-100 text-fuchsia-600', 'bg-pink-100 text-pink-600', 'bg-rose-100 text-rose-600'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  };

  const getProgramBadgeStyle = (program) => {
    if (!program) return 'bg-slate-100 text-slate-600 border-slate-200';
    const p = program.toLowerCase();
    if (p.includes('express')) return 'bg-blue-50 text-blue-700 border-blue-100';
    if (p.includes('spousal') || p.includes('parrainage')) return 'bg-pink-50 text-pink-700 border-pink-100';
    if (p.includes('study') || p.includes('permis')) return 'bg-purple-50 text-purple-700 border-purple-100';
    if (p.includes('visitor') || p.includes('visa')) return 'bg-yellow-50 text-yellow-700 border-yellow-100';
    if (p.includes('pnp') || p.includes('provincial')) return 'bg-orange-50 text-orange-700 border-orange-100';
    if (p.includes('citizen')) return 'bg-red-50 text-red-700 border-red-100';
    return 'bg-slate-100 text-slate-600 border-slate-200';
  };

  const calculateProgress = (client) => {
    if (client.fileStatus === 'complete') return 100;
    if (!client.documents || client.documents.length === 0) return 0;
    const mandatoryDocs = client.documents.filter(d => !d.optional);
    if (mandatoryDocs.length === 0) return 0;
    const uploadedCount = mandatoryDocs.filter(d => d.status === 'uploaded' || d.status === 'approved').length;
    return Math.round((uploadedCount / mandatoryDocs.length) * 100);
  };

  const getProgressColor = (percent) => {
    if (percent === 100) return 'bg-emerald-500';
    if (percent >= 60) return 'bg-blue-500';
    if (percent > 30) return 'bg-amber-500';
    return 'bg-slate-300';
  };

  // --- MISSING FUNCTION ADDED HERE ---
  const getCreationTime = (client) => {
    if (client.createdAt) return new Date(client.createdAt).getTime();
    if (client.submissionDate) return new Date(client.submissionDate).getTime();
    
    // Fallback: Extract from MongoDB _id (First 8 chars are hex timestamp)
    if (client._id && typeof client._id === 'string' && !client._id.startsWith('temp_')) {
        return parseInt(client._id.substring(0, 8), 16) * 1000;
    }
    return 0;
  };

  // Helper: Get the absolute latest interaction time (for sorting)
  const getLastTouchTime = (client) => {
      const dates = [
          client.lastViewed ? new Date(client.lastViewed).getTime() : 0,
          getCreationTime(client) // Reuse the function above
      ];
      return Math.max(...dates);
  };

  // --- RENDER PREPARATION ---

  let processedClients = (props.clients || []).filter(client => {
    const isArchived = client.archive === true;
    return showArchived ? isArchived : !isArchived;
  });

  // Sorting Logic
  processedClients.sort((a, b) => {
      // Check for temp clients (newly created, not yet saved to DB)
      const isTempA = a._id && a._id.toString().startsWith('temp_');
      const isTempB = b._id && b._id.toString().startsWith('temp_');
      
      // Always put temp clients (newly created) at the top for instant feedback
      if (isTempA && !isTempB) return -1;
      if (!isTempA && isTempB) return 1;
      
      if (mode === 'dashboard') {
        // Dashboard: recency (createdAt/submissionDate/_id vs lastViewed)
        return getLastTouchTime(b) - getLastTouchTime(a);
      }

      // Other views: recency after temp clients
      return getLastTouchTime(b) - getLastTouchTime(a);
  });

  // Search Filter
  if (mode !== 'dashboard' && searchQuery) {
      const q = searchQuery.toLowerCase();
      processedClients = processedClients.filter((c) => {
        const name = `${c.client_firstName || ''} ${c.client_lastName || ''}`.toLowerCase();
        const rawEmail = (c.client_Email || '').toLowerCase();
        const displayEmail = getClientDisplayEmail(c).toLowerCase();
        return name.includes(q) || rawEmail.includes(q) || displayEmail.includes(q);
      });
  }

  // Pagination / Slicing
  let displayClients;
  let totalPages = 1;

  if (mode === 'dashboard') {
      displayClients = processedClients.slice(0, 8);
  } else {
      totalPages = Math.ceil(processedClients.length / rowsPerPage);
      const indexOfLastRow = currentPage * rowsPerPage;
      const indexOfFirstRow = indexOfLastRow - rowsPerPage;
      displayClients = processedClients.slice(indexOfFirstRow, indexOfLastRow);
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg  border-2 w-full overflow-hidden mt-6 font-sans backdrop-blur-sm">
      
      {/* HEADER */}
      <div className="px-8 py-6 border-b border-slate-200/60 flex flex-col sm:flex-row justify-between items-center gap-4 bg-gradient-to-r from-slate-50/50 to-white">
        {mode === 'dashboard' ? (
            <>
                <div>
                    <h3 className="font-bold text-lg text-slate-900 tracking-tight">Recently Viewed Cases</h3>
                    <p className="text-sm text-slate-500 mt-1">Quick access to your active files <span className="text-xs text-slate-400 font-normal">· top 8</span></p>
                </div>
                <Link to="/dashboard/clients" className="text-sm font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1.5 transition-all hover:gap-2 px-4 py-2 rounded-lg hover:bg-blue-50">
                    See All Clients <ChevronRightIcon className="h-4 w-4" />
                </Link>
            </>
        ) : (
            <>
                <div>
                    <h3 className="font-bold text-2xl text-slate-900 tracking-tight">{title}</h3>
                    <p className="text-sm text-slate-500 mt-1">{processedClients.length} {processedClients.length === 1 ? 'client' : 'clients'}</p>
                </div>
                <div className="flex items-center gap-4">
                    <div className="relative">
                        <input 
                          type="text" 
                          placeholder="Search clients..." 
                          className="pl-10 pr-4 py-2.5  border-slate-300 rounded-xl text-sm focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 outline-none w-72 text-slate-700 bg-white shadow-sm transition-all"
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <MagnifyingGlassIcon className="h-4 w-4 text-slate-400 absolute left-3.5 top-3.5"/>
                    </div>
            
                </div>
            </>
        )}
      </div>

      {/* TABLE */}
      <div className="overflow-x-auto min-h-[500px] pb-40 bg-gradient-to-b from-white to-slate-50/30">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="text-slate-500 text-xs font-semibold uppercase tracking-wider border-b border-slate-200/60 bg-slate-50/80 backdrop-blur-sm">
              <th className="px-8 py-5 pl-8">Client</th>
              <th className="px-6 py-5">Program / Stream</th>
              <th className="px-6 py-5">Progress</th>
              <th className="px-6 py-5">Deadline</th>
              <th className="px-6 py-5 text-right pr-8">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {displayClients.map((client) => {
              
              const isTemp = client._id && client._id.toString().startsWith('temp_');
              const progress = calculateProgress(client);
              const progressColor = getProgressColor(progress);
              const initials = (client.client_firstName?.charAt(0) || "") + (client.client_lastName?.charAt(0) || "");
              const fullName = `${client.client_firstName || ""} ${client.client_lastName || ""}`;
              const avatarClass = isTemp ? 'bg-slate-100 text-slate-400' : getAvatarColor(fullName);
              const programBadgeClass = getProgramBadgeStyle(client.program);
              
              // --- THIS LINE NOW WORKS ---
              const createdTime = getCreationTime(client); 
              const isNew = !isTemp && (Date.now() - createdTime) < (24 * 60 * 60 * 1000);

              return (
                <tr 
                  key={client._id} 
                  onClick={() => handleRowClick(client._id, isTemp)}
                  className={`group transition-all duration-300 cursor-pointer hover:bg-blue-50 hover:shadow-sm`}
                >
                  <td className="px-8 py-5 pl-8 border-l-4 border-l-transparent group-hover:border-l-blue-600 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-sm shadow-md ring-2 ring-white/50 transition-transform group-hover:scale-105 ${avatarClass}`}>
                        {initials}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-800 group-hover:text-blue-600 transition-colors flex items-center gap-2 text-base">
                          {fullName}
{/*                           {isNew && <span className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-[10px] px-2 py-0.5 rounded-full uppercase font-bold tracking-wider shadow-sm">New</span>}
 */}                        </div>
                        <div className="text-sm text-slate-500 mt-0.5 truncate">{getClientDisplayEmail(client)}</div>
                      </div>
                    </div>
                  </td>

                  <td className="px-6 py-5">
                    <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold border-2 shadow-sm transition-transform group-hover:scale-105 ${programBadgeClass}`}>
                      {client.program || "General"}
                    </span>
                  </td>

                  <td className="px-6 py-5">
                    <div className="flex items-center gap-3 w-36">
                      <div className="flex-1 h-2.5 bg-slate-200 rounded-full overflow-hidden shadow-inner">
                        <div className={`h-full rounded-full transition-all duration-500 shadow-sm ${progressColor}`} style={{ width: `${progress}%` }}></div>
                      </div>
                      <span className="text-sm font-bold text-slate-700 w-10 text-right tabular-nums">{progress}%</span>
                    </div>
                  </td>

                  <td className="px-6 py-5">
                    <div className="flex items-center gap-2.5 text-slate-700">
                      <ClockIcon className={`h-4 w-4 transition-colors ${client.fileStatus === 'late' ? 'text-red-500' : 'text-slate-400'}`} />
                      <span className={`text-sm font-medium ${client.fileStatus === 'late' ? 'text-red-600 font-semibold' : 'text-slate-600'}`}>
                        {client.dateDeadline ? client.dateDeadline.split("T")[0] : "No Date"}
                      </span>
                    </div>
                  </td>

                  <td className="px-6 py-5 pr-8 text-right relative" onClick={(e) => e.stopPropagation()}>
                    {isTemp ? (
                      <div className="flex items-center justify-end gap-2 text-slate-400 italic text-sm">
                        <CircularProgress size={14} color="inherit" /> creating...
                      </div>
                    ) : (
                      <div className="relative inline-block text-left">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuRowId(openMenuRowId === client._id ? null : client._id);
                          }}
                          className="text-slate-600 hover:text-blue-600 p-2.5 rounded-lg hover:bg-blue-50 hover:shadow-md transition-all group-hover:bg-blue-50 group-hover:text-blue-600  border-transparent hover:border-blue-200"
                        >
                          <EllipsisVerticalIcon className="h-6 w-6" />
                        </button>

                        {openMenuRowId === client._id && (
                          <div ref={menuRef} className="absolute right-0 top-10 w-48 bg-white  border-slate-200 rounded-xl shadow-2xl z-50 overflow-hidden text-left animate-in fade-in zoom-in-95 duration-200 ring-1 ring-slate-900/5">
                            <button 
                                onClick={() => navigate(`/dashboard/client/${client._id}`)}
                                className="w-full text-left block px-4 py-3 text-sm font-medium text-slate-700 hover:bg-blue-50 hover:text-blue-700 flex items-center gap-3 transition-colors"
                            >
                              <EyeIcon className="h-4 w-4" /> View Details
                            </button>
                            <button 
                              onClick={() => handleArchive(client)}
                              className="w-full text-left px-4 py-3 text-sm font-medium text-slate-700 hover:bg-amber-50 hover:text-amber-700 flex items-center gap-3 transition-colors"
                            >
                              <ArchiveBoxIcon className="h-4 w-4" /> {showArchived ? 'Unarchive' : 'Archive'}
                            </button>
                            <div className="h-px bg-slate-100 my-1"></div>
                            <button 
                              onClick={() => openDeleteModal(client)}
                              className="w-full text-left px-4 py-3 text-sm font-medium text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors"
                            >
                              <TrashIcon className="h-4 w-4" /> Delete
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            
            {displayClients.length === 0 && (
                <tr>
                    <td colSpan="5" className="px-8 py-20 text-center">
                        <div className="flex flex-col items-center justify-center">
                            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                                <MagnifyingGlassIcon className="h-8 w-8 text-slate-400" />
                            </div>
                            <p className="text-slate-500 font-medium text-base">
                                {mode === 'dashboard' ? 'No recent clients found.' : 'No clients found matching your search.'}
                            </p>
                            <p className="text-slate-400 text-sm mt-1">
                                {mode === 'dashboard' ? 'Start by creating a new client.' : 'Try adjusting your search terms.'}
                            </p>
                        </div>
                    </td>
                </tr>
            )}
          </tbody>
        </table>
      </div>

      {mode !== 'dashboard' && totalPages > 1 && (
        <div className="px-8 py-5 border-t border-slate-200/60 flex justify-between items-center bg-gradient-to-r from-slate-50/50 to-white">
            <button 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="flex items-center gap-2 text-sm font-semibold text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed hover:text-blue-600 transition-all px-4 py-2 rounded-lg hover:bg-blue-50 disabled:hover:bg-transparent"
            >
                <ChevronLeftIcon className="h-4 w-4" /> Previous
            </button>
            <div className="flex items-center gap-2">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`w-8 h-8 rounded-lg text-sm font-semibold transition-all ${
                            currentPage === page
                                ? 'bg-blue-600 text-white shadow-md'
                                : 'text-slate-600 hover:bg-slate-100'
                        }`}
                    >
                        {page}
                    </button>
                ))}
            </div>
            <button 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="flex items-center gap-2 text-sm font-semibold text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed hover:text-blue-600 transition-all px-4 py-2 rounded-lg hover:bg-blue-50 disabled:hover:bg-transparent"
            >
                Next <ChevronRightIcon className="h-4 w-4" />
            </button>
        </div>
      )}

      <React.Fragment>
        <Dialog
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">{"Are you sure?"}</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Do you really want to delete this client? <br/>
              <span className="text-red-500 font-bold text-sm mt-2 block">⚠️ This action is irreversible and will delete all documents.</span>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDeleteDialog(false)} color="primary">Cancel</Button>
            <Button onClick={handleConfirmDelete} color="error" variant="contained" autoFocus>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </React.Fragment>

    </div>
  );
};

export default Datatable;